import { PlayerEnvironment } from "./player_environment";
import isFeatureToggleOn from "@javascripts/utils/feature_toggle";
import { event } from '@javascripts/events/event_name'
export default class FluidHeight {
  constructor({ tech, ads, data }) {
    this._tech = tech;
    this._ads = ads;
    this._data = data;

    this.playerContainer = null;
    this.boundOnScroll = null;
    this.boundOnModalOpen = null;
    this.boundOnModalClose = null;
    this.boundOnBannerAdsClose = null;
    this.bindedOnScroll = null;
    this.maxPlayerHeight = null; //75% height
    this.minPlayerHeight = null; //16:9 based on width
    this.rootElement = null;
    this.isModalOpen = false;
    this._bannerAdsHeight = 0;

    this.scrollLimit = 0;
    // first scrollY when player is at minHeight.
    // after player reached min height, its height doesn't change
    // so we no need to calculate scroll to add padding to content anymore
  }

  tech() {
    return this._tech;
  }

  ads() {
    return this._ads;
  }

  data() {
    return this._data;
  }

  videoEl() {
    return this.tech().videoElement();
  }

  videoAdsEl() {
    return this.ads().ima().containerEl()?.el()?.querySelector('video');
  }

  vjsEl() {
    return this.tech().el().el();
  }

  watchpageWidth() {
    return window.innerWidth < 460? window.innerWidth : 460
  }

  shouldInit() {
    return PlayerEnvironment.isMobileSite() && PlayerEnvironment.isWatchPage() && isFeatureToggleOn('enable_adaptive_player_lite');
  }

  init() {
    this.setValues();
    this.setPlayerContainer();
    this.bindAdEvents();
    this.bindLoadedmetadata();
    this.bindLiteEvent();
    this.listenScroll();
    this.bindDispose();
  }

  setValues() {
    this.boundOnScroll = this.onScroll.bind(this);
    this.boundOnModalOpen = this.onModalOpen.bind(this);
    this.boundOnModalClose = this.onModalClose.bind(this);
    this.boundOnBannerAdsClose = this.onBannerAdsClose.bind(this);
    this.rootElement = document.querySelector('#root');
    this.playerContainer = document.querySelector('.sticky-player');
    this.maxPlayerHeight = window.innerHeight * 0.75; //75% height
    this.minPlayerHeight = this.watchpageWidth() * 0.5625; //16:9 based on width
    this._bannerAdsHeight = document.querySelector('.header-ads-container')?.clientHeight || 0;

    this.setRootElementPadding(this.minPlayerHeight);
  }

  bannerAdsHeight() {
    const temp = document.querySelector('.header-ads-container')?.clientHeight;
    this._bannerAdsHeight = temp || this._bannerAdsHeight;
    return this._bannerAdsHeight;
  }

  setPlayerContainer() {
    this.playerContainer.style.position = 'fixed';
    this.playerContainer.style.top = `${this.bannerAdsHeight()}px`;
    this.playerContainer.style.width = `${this.watchpageWidth()}px`;
    this.playerContainer.style.left = '50%';
    this.playerContainer.style.transform = 'translateX(-50%)';
  }

  bindLoadedmetadata() {
    this.tech().one('loadedmetadata', this.onContentStart.bind(this));
  }

  bindContentStart() {
    this.tech().one('playing', this.onContentStart.bind(this));
  }

  onContentStart() {
    if (!this.isModalOpen) {
      this.setStart();
    }
  }

  bindLiteEvent() {
    document.addEventListener(event.lite.modal.open, this.boundOnModalOpen);
    document.addEventListener(event.lite.modal.close, this.boundOnModalClose);
    document.addEventListener(event.lite.smart_banner.close, this.boundOnBannerAdsClose);
  }

  bindDispose() {
    this.tech().on('dispose', () => {
      document.removeEventListener('scroll', this.boundOnScroll)
      document.removeEventListener(event.lite.modal.open, this.boundOnModalOpen)
      document.removeEventListener(event.lite.modal.close, this.boundOnModalClose)
      document.removeEventListener(event.lite.smart_banner.close, this.boundOnBannerAdsClose);
      this.disposeCleanUp();
    })
  }

  disposeCleanUp() {
    this.playerContainer.style = null;
    this.rootElement.style = null;
  }

  onModalOpen() {
    const scrollYToMinHeight = this.getHeightRatio() * this.watchpageWidth() - this.minPlayerHeight + this.bannerAdsHeight();
    window.scrollTo(0, scrollYToMinHeight);
    this.isModalOpen = true;
  }

  onModalClose() {
    this.isModalOpen = false;
  }

  onBannerAdsClose() {
    this._bannerAdsHeight = 0;
    this.setPlayerContainerTop(0);
  }

  setStart() {
    window.scrollTo(0, 0);
    this.scrollLimit = 0;
    this.setPaddingTop();
    this.setVideoObjectFit();
  }

  bindAdEvents() {
    this.tech().on("adsready", this.bindImaEvent.bind(this));
  }

  bindImaEvent() {
    this.ads().ima().addEventListener(google.ima.AdEvent.Type.STARTED, this.onAdStarted.bind(this));
    this.ads().ima().addEventListener(google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, this.onContentResumeRequested.bind(this));
  }

  onAdStarted() {
    if (this.isModalOpen) return;
    this.setStart();
  }

  onContentResumeRequested() {
    if (this.isModalOpen) return;
    if (PlayerEnvironment.isOSiOS()) {
      this.bindContentStart();
      return;
    }
    this.setStart();
  }

  listenScroll() {
    document.addEventListener('scroll', this.boundOnScroll);
  }

  onScroll() {
    this.setPaddingTop();
    this.setVideoObjectFit();
  }

  paddingTopValue() {
    const heightRatio = this.getHeightRatio();
    let initialHeightBasedOnWidth = heightRatio * this.watchpageWidth();
    let heightToUse = Math.min(this.maxPlayerHeight, initialHeightBasedOnWidth);
    let valAfterScroll = heightToUse - (window.scrollY);

    return Math.max(this.minPlayerHeight, valAfterScroll);
    // return value is number on px
  }

  setPaddingTop() {
    const val = this.paddingTopValue();
    this.vjsEl().style.paddingTop = `${val}px`;
    const valForLite = val / this.watchpageWidth();
    document.querySelector(":root").style.setProperty('--player-height', valForLite);

    if (window.scrollY <= this.bannerAdsHeight()) {
      //when top banner ads still visible
      this.setPlayerContainerTop((this.bannerAdsHeight() - window.scrollY));
      this.setRootElementPadding(val);
      return;
    }

    if (window.scrollY > this.bannerAdsHeight() && val > this.minPlayerHeight) {
      // when player still able to shrink
      this.setPlayerContainerTop(0);
      this.setRootElementPadding(val + window.scrollY - this.bannerAdsHeight());
      return;
    }

    if (window.scrollY > this.bannerAdsHeight() && val <= this.minPlayerHeight) {
      // when player at its minPlayerHeight (cannot shrink anymore)
      if (this.scrollLimit === 0) {
        this.scrollLimit = window.scrollY;
      }

      this.setPlayerContainerTop(0);
      this.setRootElementPadding(this.minPlayerHeight + this.scrollLimit - this.bannerAdsHeight());
    }
  }

  setVideoObjectFit() {
    const videoEl = this.videoEl();
    const heightPercentage = videoEl?.clientHeight / window.innerHeight;
    if (heightPercentage > 0.65) {
      videoEl?.classList.add('object-fit-cover');
      this.videoAdsEl()?.classList.add("object-fit-cover");
    } else {
      videoEl?.classList.remove('object-fit-cover');
      this.videoAdsEl()?.classList.remove("object-fit-cover");
    }
  }

  setRootElementPadding(value) {
    this.rootElement.style.paddingTop = `${value}px`;
  }

  setPlayerContainerTop(value) {
    this.playerContainer.style.top = `${value}px`;
  }

  getHeightRatio() {
    var result;
    if (this.tech().isInAdMode() && !this.tech().isContentResuming() && this.ads().ima().currentAd()) {
      const currentAd = this.ads().ima().currentAd();
      result = currentAd.getVastMediaHeight() / currentAd.getVastMediaWidth();
    } else {
      result = this.tech().getVideoSourceHeight() / this.tech().getVideoSourceWidth();
    }

    return this.evaluateDefaultRatio(result);
  }

  evaluateDefaultRatio(value) {
    if(isNaN(value)) {
      return 0.5625; //16:9
    } else {
      return value;
    }
  }
}
